import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import type { VendorRecord, CreateVendorRequest } from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import {
  useCreateVendor,
  useDeleteVendor,
  useDiscoverVendorModels,
  useSetVendorOfficialKey,
  useVendorOfficialKey,
  useVendorsList,
} from '../api/useVendors.ts';
import { Modal } from '../components/Modal.tsx';
import { useToast, coerceErrorMessage } from '../components/Toast.tsx';
import { useT } from '../i18n/index.ts';

const INPUT_CLS =
  'w-full bg-surface-container-lowest border-none rounded-lg text-sm p-3 outline-none focus:ring-1 focus:ring-primary transition-colors';

export function VendorsPage(): JSX.Element {
  const t = useT();
  const { data: vendors, isLoading, error } = useVendorsList();
  const createMut = useCreateVendor();
  const deleteMut = useDeleteVendor();
  const toast = useToast();

  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [keyTarget, setKeyTarget] = useState<VendorRecord | null>(null);

  const filtered = useMemo(() => {
    if (!vendors) return [];
    if (search.length === 0) return vendors;
    const q = search.toLowerCase();
    return vendors.filter(
      (v) =>
        v.slug.toLowerCase().includes(q) ||
        v.display_name.toLowerCase().includes(q),
    );
  }, [vendors, search]);

  function handleDelete(row: VendorRecord): void {
    deleteMut.mutate(row.slug, {
      onSuccess: () => toast.success(t('vendors.toast.deleted')),
      onError: (err) => toast.error(coerceErrorMessage(err)),
    });
  }

  function handleCreate(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const body: CreateVendorRequest = {
      slug: (fd.get('slug') as string).trim(),
      display_name: (fd.get('display_name') as string).trim(),
    };
    createMut.mutate(body, {
      onSuccess: () => {
        toast.success(t('vendors.toast.created'));
        setCreateOpen(false);
      },
      onError: (err) => {
        if (err instanceof OpsApiError && err.code === 'VENDOR_ALREADY_EXISTS') {
          toast.error(t('vendors.toast.duplicate'));
        } else {
          toast.error(coerceErrorMessage(err));
        }
      },
    });
  }

  if (isLoading) {
    return (
      <div data-testid="vendors-loading" className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-12 animate-pulse rounded-xl bg-surface-container" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="vendors-error" className="rounded-xl bg-error-container px-6 py-4 text-on-error-container">
        {coerceErrorMessage(error)}
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between">
        <div>
          <h2 className="text-xl font-semibold text-on-surface">{t('vendors.title')}</h2>
          <p className="mt-1 text-sm text-outline">{t('vendors.subtitle')}</p>
        </div>
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          data-testid="vendors-create-btn"
          className="flex items-center gap-2 rounded-lg bg-primary-container px-4 py-2 text-sm font-semibold text-on-primary transition-all hover:brightness-110 active:scale-95 shadow-lg shadow-primary/10"
        >
          <span aria-hidden="true" className="material-symbols-outlined text-[18px]">add</span>
          {t('vendors.create_btn')}
        </button>
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('vendors.search.placeholder')}
            data-testid="vendors-search"
            className="w-full bg-surface-container-lowest border-none rounded-lg text-sm p-3 outline-none focus:ring-1 focus:ring-primary transition-colors"
          />
          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-outline/50">
            <span aria-hidden="true" className="material-symbols-outlined text-[18px]">search</span>
          </span>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div data-testid="vendors-empty" className="rounded-xl bg-surface-container px-6 py-16 text-center text-outline">
          {t('vendors.empty')}
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl bg-surface-container" data-testid="vendors-table">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-surface-container-high/50">
                <th scope="col" className="px-6 py-4 text-[10px] font-semibold uppercase tracking-[0.1em] text-outline">Slug</th>
                <th scope="col" className="px-6 py-4 text-[10px] font-semibold uppercase tracking-[0.1em] text-outline">{t('vendors.col.display_name')}</th>
                <th scope="col" className="px-6 py-4 text-[10px] font-semibold uppercase tracking-[0.1em] text-outline">{t('vendors.col.created_at')}</th>
                <th scope="col" className="px-6 py-4 text-[10px] font-semibold uppercase tracking-[0.1em] text-outline">Official key</th>
                <th scope="col" className="px-6 py-4" />
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant/10">
              {filtered.map((v, i) => (
                <tr
                  key={v.slug}
                  data-testid={`vendor-row-${v.slug}`}
                  className={`${i % 2 === 0 ? '' : 'bg-surface-container-low/20'} hover:bg-surface-bright/30 transition-colors group`}
                >
                  <td className="px-6 py-4 text-xs font-mono text-outline">{v.slug}</td>
                  <td className="px-6 py-4 text-sm font-medium">{v.display_name}</td>
                  <td className="px-6 py-4 text-xs text-outline">{v.created_at.slice(0, 10)}</td>
                  <td className="px-6 py-4 text-xs text-outline">
                    <VendorKeyStatus slug={v.slug} />
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => setKeyTarget(v)}
                      className="text-xs font-semibold text-primary hover:underline"
                    >
                      Key
                    </button>
                    <DiscoverButton slug={v.slug} />
                    <button
                      type="button"
                      onClick={() => handleDelete(v)}
                      disabled={deleteMut.isPending}
                      className="text-outline opacity-0 group-hover:opacity-100 hover:text-error transition-all disabled:opacity-40"
                      aria-label={t('vendors.row.delete')}
                    >
                      <span aria-hidden="true" className="material-symbols-outlined text-[16px]">delete</span>
                    </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal isOpen={createOpen} onClose={() => setCreateOpen(false)} titleId="vendors-create-title">
        <div className="p-8">
          <div className="flex items-center justify-between mb-8">
            <h3 id="vendors-create-title" className="text-xl font-semibold text-on-surface">{t('vendors.create_modal.title')}</h3>
            <button
              type="button"
              onClick={() => setCreateOpen(false)}
              className="text-outline hover:text-on-surface transition-colors"
            >
              <span aria-hidden="true" className="material-symbols-outlined">close</span>
            </button>
          </div>
          <form onSubmit={handleCreate} noValidate className="space-y-6">
            <div className="space-y-1.5">
              <label className="text-[11px] font-semibold uppercase tracking-[0.08em] text-outline">SLUG</label>
              <input name="slug" className={`${INPUT_CLS} font-mono`} placeholder="e.g. anthropic" />
            </div>
            <div className="space-y-1.5">
              <label className="text-[11px] font-semibold uppercase tracking-[0.08em] text-outline">{t('vendors.create_modal.display_name')}</label>
              <input name="display_name" className={INPUT_CLS} placeholder={t('vendors.create_modal.display_name_placeholder')} />
            </div>

            {createMut.isError && (
              <div className="rounded-lg bg-error-container px-3 py-2 text-xs text-on-error-container" data-testid="vendors-create-error">
                {createMut.error instanceof OpsApiError
                  ? createMut.error.message
                  : coerceErrorMessage(createMut.error)}
              </div>
            )}

            <div className="flex items-center justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={() => setCreateOpen(false)}
                className="px-6 py-2.5 rounded-lg text-sm font-semibold text-on-surface-variant hover:bg-surface-variant transition-all"
              >
                {t('vendors.create_modal.cancel')}
              </button>
              <button
                type="submit"
                disabled={createMut.isPending}
                className="px-8 py-2.5 rounded-lg text-sm font-semibold bg-primary-container text-on-primary hover:brightness-110 active:scale-95 transition-all shadow-lg shadow-primary/10 disabled:opacity-40 disabled:pointer-events-none"
              >
                {createMut.isPending ? t('vendors.create_modal.creating') : t('vendors.create_modal.submit')}
              </button>
            </div>
          </form>
        </div>
      </Modal>

      {keyTarget !== null ? (
        <VendorKeyModal vendor={keyTarget} onClose={() => setKeyTarget(null)} />
      ) : null}
    </div>
  );
}

function VendorKeyStatus({ slug }: { readonly slug: string }): JSX.Element {
  const key = useVendorOfficialKey(slug);
  if (key.isLoading) {
    return <span>...</span>;
  }
  if (key.isError) {
    return <span className="text-error">error</span>;
  }
  const credential = key.data?.credential;
  return (
    <span>
      {credential?.has_key ? credential.key_preview ?? 'configured' : 'missing'}
    </span>
  );
}

function DiscoverButton({ slug }: { readonly slug: string }): JSX.Element {
  const discover = useDiscoverVendorModels();
  const toast = useToast();
  return (
    <button
      type="button"
      disabled={discover.isPending}
      onClick={() => {
        discover.mutate(slug, {
          onSuccess: (res) => toast.success(`发现完成：seen ${res.seen}, missing ${res.missing}`),
          onError: (err) => toast.error(coerceErrorMessage(err)),
        });
      }}
      className="text-xs font-semibold text-primary hover:underline disabled:opacity-40"
    >
      Discover
    </button>
  );
}

function VendorKeyModal({
  vendor,
  onClose,
}: {
  readonly vendor: VendorRecord;
  readonly onClose: () => void;
}): JSX.Element {
  const setKey = useSetVendorOfficialKey();
  const key = useVendorOfficialKey(vendor.slug);
  const toast = useToast();

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const apiKey = (fd.get('api_key') as string).trim();
    if (apiKey.length === 0) {
      toast.error('API key 不能为空');
      return;
    }
    setKey.mutate({ slug: vendor.slug, api_key: apiKey }, {
      onSuccess: () => {
        toast.success('官方 API key 已保存');
        onClose();
      },
      onError: (err) => toast.error(coerceErrorMessage(err)),
    });
  }

  return (
    <Modal isOpen onClose={onClose} titleId="vendor-key-title">
      <form onSubmit={handleSubmit} noValidate className="space-y-6 p-8">
        <header className="flex items-center justify-between">
          <div>
            <h3 id="vendor-key-title" className="text-xl font-semibold text-on-surface">
              {vendor.display_name}
            </h3>
            <p className="mt-1 text-xs text-outline">
              {key.data?.credential.has_key
                ? `当前 key: ${key.data.credential.key_preview ?? 'configured'}`
                : '尚未配置官方发现 key'}
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-outline hover:text-on-surface">
            <span aria-hidden="true" className="material-symbols-outlined">close</span>
          </button>
        </header>
        <label className="space-y-1.5 block">
          <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-outline">
            Official API key
          </span>
          <input
            name="api_key"
            type="password"
            autoComplete="off"
            className={INPUT_CLS}
            placeholder="sk-..."
          />
        </label>
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-6 py-2.5 text-sm font-semibold text-on-surface-variant hover:bg-surface-variant"
          >
            取消
          </button>
          <button
            type="submit"
            disabled={setKey.isPending}
            className="rounded-lg bg-primary-container px-8 py-2.5 text-sm font-semibold text-on-primary disabled:opacity-40"
          >
            {setKey.isPending ? '保存中' : '保存'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
